import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Typography,
  Box,
  FormHelperText,
  CircularProgress,
  Divider,
  IconButton,
  useTheme
} from '@mui/material';
import { Close, Save, Edit } from '@mui/icons-material';

const EditUser = ({ open, onClose, onSubmit, user, branches, isLoading }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  
  // Form state
  const [formData, setFormData] = useState({
    account_id: '',
    username: '',
    first_name: '',
    last_name: '',
    email: '',
    branch_id: '',
    role_id: ''
  });
  
  // Validation state
  const [errors, setErrors] = useState({});
  
  // Update form when user changes
  useEffect(() => {
    if (user) {
      console.log("EditUser received:", user);
      console.log("Branch and role IDs:", user.branch_id, user.role_id);
      setFormData({
        account_id: user.account_id || '',
        username: user.username || '',
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
        branch_id: String(user.branch_id || ''),
        role_id: String(user.role_id || '')
      });
    }
  }, [user]);
  
  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: ''
      }));
    }
  };
  
  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    // Required fields
    if (!formData.username) newErrors.username = t('user.add.usernameRequired');
    if (!formData.email) {
      newErrors.email = t('user.add.emailRequired');
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t('user.add.emailInvalid');
    }
    if (!formData.branch_id) newErrors.branch_id = t('user.add.branchRequired');
    if (!formData.role_id) newErrors.role_id = t('user.add.roleRequired');
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Handle submit
  const handleSubmit = () => {
    if (validateForm()) {
      onSubmit(formData);
    }
  };
  
  // Handle close without saving
  const handleClose = () => {
    setErrors({});
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: { 
          borderRadius: 2,
          overflow: 'hidden',
          backgroundColor: isDarkMode ? '#1e1e1e' : '#ffffff',
        }
      }}
    >
      <DialogTitle 
        sx={{ 
          background: isDarkMode 
            ? 'linear-gradient(135deg, #2563eb 0%, #1e3a8a 100%)'
            : 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Edit />
          <Typography variant="h6" sx={{ color: 'white' }}>
            {t('user.edit.title')}
          </Typography>
        </Box>
        <IconButton size="small" onClick={handleClose} sx={{ color: 'white' }}>
          <Close />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ 
        p: 3,
        backgroundColor: isDarkMode ? '#1e1e1e' : '#ffffff',
        color: isDarkMode ? '#ffffff' : 'inherit'
      }}>
        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Typography 
                variant="subtitle1" 
                fontWeight={600} 
                sx={{ color: isDarkMode ? '#ffffff' : theme.palette.primary.main }}
              >
                {t('user.add.accountInfo')}
              </Typography>
              <Divider sx={{ flexGrow: 1, ml: 2, borderColor: isDarkMode ? '#404040' : 'inherit' }} />
            </Box>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t('user.edit.accountId')}
              value={formData.account_id}
              disabled
              InputProps={{
                sx: { 
                  borderRadius: 1.5,
                  backgroundColor: isDarkMode ? '#2a2a2a' : '#f5f5f5',
                  color: isDarkMode ? '#b3b3b3' : 'inherit',
                }
              }}
              InputLabelProps={{
                sx: { color: isDarkMode ? '#b3b3b3' : 'inherit' }
              }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t('user.add.username')}
              name="username"
              value={formData.username}
              onChange={handleChange}
              error={!!errors.username}
              helperText={errors.username}
              required
              InputProps={{
                sx: { 
                  borderRadius: 1.5,
                  backgroundColor: isDarkMode ? '#2a2a2a' : '#ffffff',
                  color: isDarkMode ? '#ffffff' : 'inherit',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: isDarkMode ? '#404040' : 'rgba(0, 0, 0, 0.23)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: isDarkMode ? '#3b82f6' : 'rgba(0, 0, 0, 0.87)',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#3b82f6',
                  }
                }
              }}
              InputLabelProps={{
                sx: { color: isDarkMode ? '#b3b3b3' : 'inherit' }
              }}
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t('user.add.email')}
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              error={!!errors.email}
              helperText={errors.email}
              required
              InputProps={{
                sx: { 
                  borderRadius: 1.5,
                  backgroundColor: isDarkMode ? '#2a2a2a' : '#ffffff',
                  color: isDarkMode ? '#ffffff' : 'inherit',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: isDarkMode ? '#404040' : 'rgba(0, 0, 0, 0.23)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: isDarkMode ? '#3b82f6' : 'rgba(0, 0, 0, 0.87)',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#3b82f6',
                  }
                }
              }}
              InputLabelProps={{
                sx: { color: isDarkMode ? '#b3b3b3' : 'inherit' }
              }}
            />
          </Grid>
          
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, mt: 1 }}>
              <Typography 
                variant="subtitle1" 
                fontWeight={600} 
                sx={{ color: isDarkMode ? '#ffffff' : theme.palette.primary.main }}
              >
                {t('user.add.personalInfo')}
              </Typography>
              <Divider sx={{ flexGrow: 1, ml: 2, borderColor: isDarkMode ? '#404040' : 'inherit' }} />
            </Box>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t('user.add.firstName')}
              name="first_name"
              value={formData.first_name}
              onChange={handleChange}
              InputProps={{
                sx: { 
                  borderRadius: 1.5,
                  backgroundColor: isDarkMode ? '#2a2a2a' : '#ffffff',
                  color: isDarkMode ? '#ffffff' : 'inherit',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: isDarkMode ? '#404040' : 'rgba(0, 0, 0, 0.23)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: isDarkMode ? '#3b82f6' : 'rgba(0, 0, 0, 0.87)',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#3b82f6',
                  }
                }
              }}
              InputLabelProps={{
                sx: { color: isDarkMode ? '#b3b3b3' : 'inherit' }
              }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t('user.add.lastName')}
              name="last_name"
              value={formData.last_name}
              onChange={handleChange}
              InputProps={{
                sx: { 
                  borderRadius: 1.5,
                  backgroundColor: isDarkMode ? '#2a2a2a' : '#ffffff',
                  color: isDarkMode ? '#ffffff' : 'inherit',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: isDarkMode ? '#404040' : 'rgba(0, 0, 0, 0.23)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: isDarkMode ? '#3b82f6' : 'rgba(0, 0, 0, 0.87)',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#3b82f6',
                  }
                }
              }}
              InputLabelProps={{
                sx: { color: isDarkMode ? '#b3b3b3' : 'inherit' }
              }}
            />
          </Grid>
          
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, mt: 1 }}>
              <Typography 
                variant="subtitle1" 
                fontWeight={600} 
                sx={{ color: isDarkMode ? '#ffffff' : theme.palette.primary.main }}
              >
                {t('user.add.roleAndBranch')}
              </Typography>
              <Divider sx={{ flexGrow: 1, ml: 2, borderColor: isDarkMode ? '#404040' : 'inherit' }} />
            </Box>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth error={!!errors.branch_id} required>
              <InputLabel sx={{ color: isDarkMode ? '#b3b3b3' : 'inherit' }}>
                {t('user.branch')}
              </InputLabel>
              <Select
                name="branch_id"
                value={formData.branch_id}
                onChange={handleChange}
                label={t('user.branch')}
                sx={{ 
                  borderRadius: 1.5,
                  backgroundColor: isDarkMode ? '#2a2a2a' : '#ffffff',
                  color: isDarkMode ? '#ffffff' : 'inherit',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: isDarkMode ? '#404040' : 'rgba(0, 0, 0, 0.23)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: isDarkMode ? '#3b82f6' : 'rgba(0, 0, 0, 0.87)',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#3b82f6',
                  }
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      backgroundColor: isDarkMode ? '#2a2a2a' : '#ffffff',
                      '& .MuiMenuItem-root': {
                        color: isDarkMode ? '#ffffff' : 'inherit',
                        '&:hover': {
                          backgroundColor: isDarkMode ? '#404040' : 'rgba(0, 0, 0, 0.04)',
                        }
                      }
                    }
                  }
                }}
              >
                {branches.map(branch => (
                  <MenuItem key={branch.branch_id} value={branch.branch_id.toString()}>
                    {branch.location_name}
                  </MenuItem>
                ))}
              </Select>
              {errors.branch_id && (
                <FormHelperText>{errors.branch_id}</FormHelperText>
              )}
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth error={!!errors.role_id} required>
              <InputLabel sx={{ color: isDarkMode ? '#b3b3b3' : 'inherit' }}>
                {t('user.role')}
              </InputLabel>
              <Select
                name="role_id"
                value={formData.role_id}
                onChange={handleChange}
                label={t('user.role')}
                sx={{ 
                  borderRadius: 1.5,
                  backgroundColor: isDarkMode ? '#2a2a2a' : '#ffffff',
                  color: isDarkMode ? '#ffffff' : 'inherit',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: isDarkMode ? '#404040' : 'rgba(0, 0, 0, 0.23)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: isDarkMode ? '#3b82f6' : 'rgba(0, 0, 0, 0.87)',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#3b82f6',
                  }
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      backgroundColor: isDarkMode ? '#2a2a2a' : '#ffffff',
                      '& .MuiMenuItem-root': {
                        color: isDarkMode ? '#ffffff' : 'inherit',
                        '&:hover': {
                          backgroundColor: isDarkMode ? '#404040' : 'rgba(0, 0, 0, 0.04)',
                        }
                      }
                    }
                  }
                }}
              >
                <MenuItem value="1">{t('user.admin')}</MenuItem>
                <MenuItem value="2">{t('user.manager')}</MenuItem>
                <MenuItem value="3">{t('user.staff')}</MenuItem>
              </Select>
              {errors.role_id && (
                <FormHelperText>{errors.role_id}</FormHelperText>
              )}
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>
      
      <DialogActions sx={{ 
        p: 2, 
        backgroundColor: isDarkMode ? '#2a2a2a' : theme.palette.grey[50],
        borderTop: `1px solid ${isDarkMode ? '#404040' : 'rgba(0, 0, 0, 0.12)'}`
      }}>
        <Button 
          onClick={handleClose}
          variant="outlined"
          sx={{ 
            borderRadius: 1.5,
            color: isDarkMode ? '#ffffff' : 'inherit',
            borderColor: isDarkMode ? '#404040' : 'rgba(0, 0, 0, 0.23)',
            '&:hover': {
              borderColor: isDarkMode ? '#3b82f6' : 'rgba(0, 0, 0, 0.87)',
              backgroundColor: isDarkMode ? 'rgba(59, 130, 246, 0.1)' : 'rgba(0, 0, 0, 0.04)',
            }
          }}
          disabled={isLoading}
        >
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isLoading}
          sx={{ 
            borderRadius: 1.5,
            background: isDarkMode
              ? 'linear-gradient(135deg, #2563eb 0%, #1e3a8a 100%)'
              : 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
            '&:hover': {
              background: isDarkMode
                ? 'linear-gradient(135deg, #1d4ed8 0%, #1e3a8a 100%)'
                : 'linear-gradient(135deg, #2563eb 0%, #1e3a8a 100%)'
            }
          }}
          startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <Save />}
        >
          {isLoading ? t('user.edit.saving') : t('user.edit.saveButton')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditUser;